/**
 * Course Form Component
 * Task 52: Form for creating/editing courses with pricing, capacity, schedule, installments
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { IndustryCourse } from '@/types/industry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const courseSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  price: z.coerce.number().min(0),
  duration: z.coerce.number().min(0).optional(),
  capacity: z.coerce.number().min(0).optional(),
  schedule: z.string().optional(),
  installments: z.coerce.number().min(1).max(12).optional(),
  category: z.string().optional(),
  is_active: z.boolean().default(true),
  description: z.string().optional(),
});

type CourseFormData = z.infer<typeof courseSchema>;

interface CourseFormProps {
  course?: IndustryCourse;
  existingCategories?: string[];
  onSubmit: (data: CourseFormData) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

const DURATION_PRESETS = [
  { value: 60, label: '1 hora' },
  { value: 90, label: '1h 30min' },
  { value: 120, label: '2 horas' },
  { value: 180, label: '3 horas' },
  { value: 240, label: '4 horas' },
  { value: 480, label: '8 horas' },
  { value: 960, label: '16 horas' },
  { value: 1440, label: '1 dia (24h)' },
  { value: 2880, label: '2 dias' },
  { value: 4320, label: '3 dias' },
];

const COMMON_CATEGORIES = [
  'Idiomas',
  'Tecnologia',
  'Negócios',
  'Saúde',
  'Arte e Design',
  'Música',
  'Esportes',
  'Culinária',
  'Fotografia',
  'Marketing',
];

export function CourseForm({ course, existingCategories = [], onSubmit, onCancel, isSubmitting = false }: CourseFormProps) {
  const courseMetadata = (course?.metadata || {}) as Record<string, unknown>;
  const defaultDuration =
    typeof courseMetadata.duration === 'number'
      ? (courseMetadata.duration as number)
      : typeof course?.duration_hours === 'number'
        ? course.duration_hours * 60
        : undefined;
  const defaultCapacity =
    typeof courseMetadata.capacity === 'number'
      ? (courseMetadata.capacity as number)
      : typeof course?.max_students === 'number'
        ? course.max_students
        : undefined;
  const defaultInstallments =
    typeof courseMetadata.installments === 'number'
      ? (courseMetadata.installments as number)
      : typeof course?.max_installments === 'number'
        ? course.max_installments
        : 1;
  const defaultCategory =
    typeof courseMetadata.category === 'string' ? (courseMetadata.category as string) : '';
  const defaultSchedule =
    typeof courseMetadata.schedule === 'string' ? (courseMetadata.schedule as string) : '';
  const defaultDescription =
    typeof course?.description === 'string' && course.description
      ? course.description
      : (courseMetadata.description as string) || '';
  const defaultIsActive =
    course?.is_active ?? ['published', 'in_progress'].includes(course?.status ?? 'draft');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CourseFormData>({
    resolver: zodResolver(courseSchema),
    defaultValues: course
      ? {
          name: course.name || '',
          price: course.price || 0,
          duration: defaultDuration,
          capacity: defaultCapacity,
          schedule: defaultSchedule,
          installments: defaultInstallments,
          category: defaultCategory,
          is_active: defaultIsActive,
          description: defaultDescription,
        }
      : {
          is_active: true,
          installments: 1,
          price: 0,
        },
  });

  const duration = watch('duration');
  const isActive = watch('is_active');
  const selectedCategory = watch('category');

  // Combine existing categories with common categories (unique)
  const allCategories = Array.from(new Set([...existingCategories, ...COMMON_CATEGORIES])).sort();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nome do Curso *</Label>
              <Input {...register('name')} placeholder="Ex: Inglês Avançado" />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Área do Conhecimento</Label>
                <Select value={selectedCategory} onValueChange={(v) => setValue('category', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a área" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Ou digite uma nova área acima</p>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    checked={isActive}
                    onCheckedChange={(checked) => setValue('is_active', checked)}
                  />
                  <Label className="cursor-pointer" onClick={() => setValue('is_active', !isActive)}>
                    {isActive ? 'Ativo' : 'Inativo'}
                  </Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea {...register('description')} rows={3} placeholder="Descrição do curso" />
            </div>
          </CardContent>
        </Card>

        {/* Schedule and Capacity */}
        <Card>
          <CardHeader>
            <CardTitle>Horário e Capacidade</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duração</Label>
                <Select
                  value={duration?.toString()}
                  onValueChange={(v) => setValue('duration', v === 'custom' ? undefined : parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a duração" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value.toString()}>
                        {preset.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
                {duration === undefined && (
                  <Input
                    type="number"
                    {...register('duration')}
                    placeholder="Duração em minutos"
                    className="mt-2"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label>Número de Vagas</Label>
                <Input type="number" {...register('capacity')} placeholder="Ex: 30" />
                <p className="text-xs text-muted-foreground">Deixe em branco para ilimitado</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Horário das Aulas</Label>
              <Input
                {...register('schedule')}
                placeholder="Ex: Segundas e Quartas, 19h-21h"
              />
              <p className="text-xs text-muted-foreground">
                Dias da semana e horários das aulas
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle>Valores</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valor Total (R$) *</Label>
                <Input type="number" step="0.01" {...register('price')} />
                {errors.price && <p className="text-sm text-destructive">{errors.price.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Número de Parcelas</Label>
                <Select
                  value={watch('installments')?.toString()}
                  onValueChange={(v) => setValue('installments', parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">À vista</SelectItem>
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                      <SelectItem key={n} value={n.toString()}>
                        {n}x
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {watch('installments') && watch('installments')! > 1 && watch('price') > 0 && (
              <div className="rounded-md bg-muted p-4">
                <p className="text-sm font-medium">
                  Valor da parcela:{' '}
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(watch('price') / watch('installments')!)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : course ? 'Atualizar' : 'Criar'} Curso
          </Button>
        </div>
      </div>
    </form>
  );
}
